import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuthStore } from '../store/authStore'
import { resolveEntitlementPlan } from '../lib/planEntitlement'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

export interface SubscriptionPlan {
  id: string
  name: string
  monthly_price: number
  annual_price: number
  trial_days: number
  max_users: number
  max_locations: number
  max_products: number
  max_vertical_modules: number
  included_modules: string[]
  included_reports: string[]
  features: Record<string, unknown>
  is_active: boolean
  is_visible: boolean
}

interface TenantOverride {
  override_type: 'module' | 'report'
  resource_code: string
  expires_at: string | null
  revoked_at: string | null
}

export function useTenantAccess() {
  const { tenant } = useAuthStore()
  const tid = tenant?.id ?? ''
  const planId = tenant?.plan ?? 'starter'
  // SUB.4: trial resolves to the top tier's entitlements (Pro) for BOTH pages and
  // modules — instead of the old no-row lockout. Real plans resolve to themselves;
  // the ?? 'starter' above stays the ultimate fallback for a missing plan.
  const effectivePlan = resolveEntitlementPlan(planId) ?? 'starter'

  const { data: plan = null, isLoading: loadingPlan } = useQuery<SubscriptionPlan | null>({
    queryKey: ['subscription-plan', effectivePlan],
    enabled: !!effectivePlan,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await db('subscription_plans')
        .select('id, name, monthly_price, annual_price, trial_days, max_users, max_locations, max_products, max_vertical_modules, included_modules, included_reports, features, is_active, is_visible')
        .eq('id', effectivePlan)
        .maybeSingle()
      return data ?? null
    },
  })

  const { data: overrides = [], isLoading: loadingOverrides } = useQuery<TenantOverride[]>({
    queryKey: ['tenant-overrides', tid],
    enabled: !!tid,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const now = new Date().toISOString()
      const { data } = await db('tenant_overrides')
        .select('override_type, resource_code, expires_at, revoked_at')
        .eq('tenant_id', tid)
        .is('revoked_at', null)
      return (data ?? []).filter((o: TenantOverride) => !o.expires_at || o.expires_at > now)
    },
  })

  const { data: planPages = new Set<string>(), isLoading: loadingPages } = useQuery<Set<string>>({
    queryKey: ['plan-pages', tid, effectivePlan],
    enabled: !!effectivePlan,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      // Option A — effective = plan TEMPLATE ∪ per-tenant SNAPSHOT.
      // Start from the live template (plan_pages) so a page ADDED to a tier reaches
      // existing tenants immediately (no "the new feature is missing" gap). Then union
      // in the tenant's frozen snapshot (tenant_plan_pages, mig 348/349) so a page
      // REMOVED from a tier is RETAINED until their next renewal re-snapshots them —
      // Option A's no-mid-cycle-takeaway promise, without hiding new pages.
      // Fail-soft: the template read is primary (throws like today); the snapshot read
      // is best-effort — if the table is absent / RLS-blocked / errors, we keep the
      // template set, so this change is inert until the migration + backfill land.
      const { data, error } = await db('plan_pages')
        .select('page_key')
        .eq('plan_id', effectivePlan)
      if (error) throw error
      const eff = new Set<string>(((data ?? []) as { page_key: string }[]).map(r => r.page_key))
      if (tid) {
        const { data: snap, error: snapErr } = await db('tenant_plan_pages')
          .select('page_key')
          .eq('tenant_id', tid)
        if (!snapErr && Array.isArray(snap)) {
          for (const r of snap as { page_key: string }[]) eff.add(r.page_key)
        }
      }
      return eff
    },
  })

  function hasPage(key: string): boolean {
    return planPages.has(key)
  }

  const includedModules: string[] = plan?.included_modules ?? []
  const includedReports: string[] = plan?.included_reports ?? []

  function hasModule(code: string): boolean {
    return includedModules.includes(code)
      || overrides.some(o => o.override_type === 'module' && o.resource_code === code)
  }

  function hasReport(code: string): boolean {
    return includedReports.includes(code)
      || overrides.some(o => o.override_type === 'report' && o.resource_code === code)
  }

  const accessibleModules = [
    ...includedModules,
    ...overrides.filter(o => o.override_type === 'module').map(o => o.resource_code),
  ].filter((v, i, a) => a.indexOf(v) === i)

  const accessibleReports = [
    ...includedReports,
    ...overrides.filter(o => o.override_type === 'report').map(o => o.resource_code),
  ].filter((v, i, a) => a.indexOf(v) === i)

  return {
    hasModule,
    hasReport,
    hasPage,
    accessibleModules,
    accessibleReports,
    accessiblePages: planPages,
    plan,
    isLoading: loadingPlan || loadingOverrides,
    loadingPages,
  }
}
